import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Subscriptions as SubscriptionsIcon,
  VpnKey as VpnKeyIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  ManageAccounts as ManageAccountsIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StatCardProps } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService, DashboardStatistics } from '../services/dashboardService';
import { PERMISSIONS } from '../utils/permissionAccess';

const StatCard: React.FC<StatCardProps & { onClick?: () => void }> = ({ title, value, icon, color, onClick }) => (
  <Card 
    sx={{ 
      height: '100%', 
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': onClick ? {
        transform: 'translateY(-2px)',
        boxShadow: 3,
      } : {},
    }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            backgroundColor: color,
            borderRadius: '50%',
            p: 1,
            mr: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </CardContent>
  </Card>
);

interface ActivityItem {
  action: string;
  time: string;
  type: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { tokens, login, user } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Get user permissions
  const userPermissions = user?.permissions || [];

  // Initialize service and load statistics
  useEffect(() => {
    if (tokens && login) {
      dashboardService.initialize(tokens, login);
      loadStatistics();
    }
  }, [tokens, login]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await dashboardService.getStatistics();
      
      if (response.succeeded && response.data) {
        setStatistics(response.data);
      } else {
        setError(response.errorDescription || 'Failed to load dashboard statistics');
      }
    } catch (err) {
      console.error('Error loading dashboard statistics:', err);
      setError('Failed to load dashboard statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format number with commas
  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  // Stats cards configuration
  const stats = [
    {
      title: 'Total Users',
      value: formatNumber(statistics?.totalUsers),
      icon: <PeopleIcon sx={{ color: 'white' }} />,
      color: '#1976d2',
      onClick: () => navigate('/users'),
    },
    {
      title: 'Active Tenants',
      value: formatNumber(statistics?.totalTenants),
      icon: <BusinessIcon sx={{ color: 'white' }} />,
      color: '#4caf50',
      onClick: () => navigate('/tenants'),
    },
    {
      title: 'Subscriptions',
      value: formatNumber(statistics?.totalSubscriptions),
      icon: <SubscriptionsIcon sx={{ color: 'white' }} />,
      color: '#ff9800',
      onClick: () => navigate('/subscriptions'),
    },
    {
      title: 'Roles',
      value: formatNumber(statistics?.totalRoles),
      icon: <SecurityIcon sx={{ color: 'white' }} />,
      color: '#9c27b0',
      onClick: () => navigate('/roles'),
    },
    {
      title: 'Permissions',
      value: formatNumber(statistics?.totalPermissions),
      icon: <VpnKeyIcon sx={{ color: 'white' }} />,
      color: '#f44336',
      onClick: () => navigate('/permissions'),
    },
    {
      title: 'Active Sessions',
      value: 'N/A',
      icon: <TrendingUpIcon sx={{ color: 'white' }} />,
      color: '#00bcd4',
      onClick: () => navigate('/users'), // Sessions would be part of users
    },
  ];

  const recentActivity: ActivityItem[] = [
    { action: 'New user registered', time: '2 minutes ago', type: 'user' },
    { action: 'Subscription renewed', time: '15 minutes ago', type: 'subscription' },
    { action: 'Role permissions updated', time: '1 hour ago', type: 'role' },
    { action: 'Tenant configuration changed', time: '2 hours ago', type: 'tenant' },
    { action: 'Password reset requested', time: '3 hours ago', type: 'user' },
  ];

  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(userPerm => 
      userPerm.toLowerCase() === permission.toLowerCase() ||
      userPerm === permission
    );
  };

  // Quick Actions configuration with required permissions
  const allQuickActions = [
    {
      title: 'Add New User',
      description: 'Create a new user account',
      icon: <AddIcon />,
      action: () => navigate('/users'),
      color: '#1976d2',
      requiredPermission: PERMISSIONS.ADD_USER,
    },
    {
      title: 'Create Subscription',
      description: 'Set up a new subscription',
      icon: <SubscriptionsIcon />,
      action: () => navigate('/subscriptions'),
      color: '#ff9800',
      requiredPermission: PERMISSIONS.CREATE_SUBSCRIPTION,
    },
    {
      title: 'Configure Tenant',
      description: 'Manage tenant settings',
      icon: <SettingsIcon />,
      action: () => navigate('/tenants'),
      color: '#4caf50',
      requiredPermission: PERMISSIONS.CREATE_TENANT, // User needs create tenant permission
    },
    {
      title: 'Manage Roles',
      description: 'Configure user roles',
      icon: <ManageAccountsIcon />,
      action: () => navigate('/roles'),
      color: '#9c27b0',
      requiredPermission: PERMISSIONS.CREATE_ROLE, // User needs create role permission
    },
    {
      title: 'Review Permissions',
      description: 'Manage system permissions',
      icon: <KeyIcon />,
      action: () => navigate('/permissions'),
      color: '#f44336',
      requiredPermission: PERMISSIONS.CREATE_PERMISSION, // User needs create permission
    },
  ];

  // Filter quick actions based on user permissions
  const quickActions = allQuickActions.filter(action => 
    hasPermission(action.requiredPermission)
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome to the Identity Server Portal. Here's an overview of your system.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2, mb: 4 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StatCard 
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                color={stat.color}
                onClick={stat.onClick}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Box>
              {recentActivity.map((activity, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: index < recentActivity.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  <Typography variant="body2">{activity.action}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activity.time}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <List>
              {quickActions.map((action, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    onClick={action.action}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: `${action.color}15`,
                        '& .MuiListItemIcon-root': {
                          color: action.color,
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: action.color }}>
                      {action.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={action.title}
                      secondary={action.description}
                      primaryTypographyProps={{
                        variant: 'body1',
                        fontWeight: 500,
                      }}
                      secondaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.secondary',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 